import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const RefundPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-gray-100 dark:bg-slate-900 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <button onClick={() => navigate('/')} className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </button>
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Refund Policy</CardTitle>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-2">Last Updated: January 26, 2025</p>
          </CardHeader>
          <CardContent className="prose dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300">
              At SmartPDF Tools, we strive to provide excellent service. This Refund Policy outlines our refund terms for different subscription plans.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. General Refund Policy</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>7-Day Money-Back Guarantee:</strong> For first-time subscribers to Pro and Yearly plans</li>
              <li><strong>EU Consumer Rights:</strong> 14-day cooling-off period for EU customers</li>
              <li><strong>No Refunds for Used Services:</strong> If you've processed files after purchase</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Refund by Plan Type</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">2.1 Pro Plan ($5/month)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>✅ <strong>Refundable:</strong> Within 7 days if unused</li>
              <li>❌ <strong>Not Refundable:</strong> After 7 days or if files have been processed</li>
              <li><strong>Cancellation:</strong> Can cancel anytime; access continues until period ends</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">2.2 Yearly Plan ($50/year)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>✅ <strong>Refundable:</strong> Within 7 days (or 14 days for EU) if unused</li>
              <li>❌ <strong>Not Refundable:</strong> After refund window or if files processed</li>
              <li><strong>Pro-Rated:</strong> No pro-rated refunds after refund window</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">2.3 Lifetime Plan ($39 one-time)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>✅ <strong>Refundable:</strong> Within 7 days (or 14 days for EU) if unused</li>
              <li>❌ <strong>Not Refundable:</strong> After refund window</li>
              <li><strong>Permanent Access:</strong> Once refund window passes, purchase is final</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Refund Eligibility</h2>
            <p className="text-slate-700 dark:text-slate-300"><strong>Refunds are available if:</strong></p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li>Request made within refund window (7-14 days)</li>
              <li>Account shows minimal or no usage (0 files processed)</li>
              <li>Technical issues prevented service use (we'll investigate)</li>
              <li>Accidental purchase (must request immediately)</li>
            </ul>

            <p className="text-slate-700 dark:text-slate-300 mt-6"><strong>Refunds are NOT available if:</strong></p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li>You've processed PDF files using the service</li>
              <li>Request made after refund window</li>
              <li>Account violations or Terms of Service breaches</li>
              <li>Changed your mind after using the service</li>
              <li>Subscription auto-renewed (cancel to prevent next renewal)</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. How to Request a Refund</h2>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700 dark:text-slate-300">
              <li><strong>Email us:</strong> Send refund request to <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Include:</strong> Account email, purchase date, reason for refund</li>
              <li><strong>Processing:</strong> We'll review within 2-3 business days</li>
              <li><strong>Approval:</strong> If approved, refund processed within 5-10 business days</li>
              <li><strong>Method:</strong> Refunded to original payment method via Stripe</li>
            </ol>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. EU Consumer Rights</h2>
            <p className="text-slate-700 dark:text-slate-300">
              EU customers have a <strong>14-day cooling-off period</strong> under EU Consumer Rights Directive:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li>Right to cancel within 14 days without reason</li>
              <li>Applies to first purchase only</li>
              <li><strong>Exception:</strong> Right lost if you use the service (process files)</li>
              <li>Must explicitly acknowledge waiver when purchasing</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Cancellations vs. Refunds</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">6.1 Cancellation (No Refund)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Cancel subscription anytime</li>
              <li>Access continues until end of billing period</li>
              <li>No refund for remaining time</li>
              <li>No auto-renewal for next period</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.2 Refund (Money Back)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Must meet eligibility criteria</li>
              <li>Within refund window</li>
              <li>Account closed upon refund</li>
              <li>Revert to free plan</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Disputed Charges</h2>
            <p className="text-slate-700 dark:text-slate-300">
              If you see an unexpected charge:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Contact Us First:</strong> Email <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Don't Dispute Immediately:</strong> We can resolve faster than chargebacks</li>
              <li><strong>Check Auto-Renewal:</strong> Subscriptions renew automatically</li>
              <li><strong>Chargeback Warning:</strong> May result in account closure</li>
            </ol>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. Refund Processing Time</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Review:</strong> 2-3 business days</li>
              <li><strong>Approval:</strong> 1 business day</li>
              <li><strong>Stripe Processing:</strong> 5-10 business days</li>
              <li><strong>Bank Posting:</strong> Additional 2-5 business days</li>
              <li><strong>Total Time:</strong> Up to 15 business days</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Contact for Refunds</h2>
            <p className="text-slate-700 dark:text-slate-300">
              For refund requests or questions:
            </p>
            <ul className="list-none pl-0 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Email:</strong> <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Support:</strong> <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Response Time:</strong> Within 24-48 hours</li>
            </ul>

            <div className="mt-12 p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <strong>💡 Pro Tip:</strong> Cancel before renewal to avoid charges. Set a calendar reminder! You'll keep access until the period ends.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RefundPolicy;